import React from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
  success?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  containerClassName?: string;
}

const Input: React.FC<InputProps> = ({
  label,
  helperText,
  error,
  success,
  leftIcon,
  rightIcon,
  containerClassName = '',
  className = '',
  id,
  ...props
}) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const hasError = Boolean(error);
  const hasSuccess = Boolean(success) && !hasError;
  const stateClasses = hasError
    ? 'border-[var(--theme-danger)] focus-visible:ring-[var(--theme-danger)]'
    : hasSuccess
      ? 'border-theme-accent focus-visible:ring-theme-accent'
      : 'border-theme-border focus-visible:ring-theme-info';
  const helperId = inputId ? `${inputId}-helper` : undefined;
  const stateId = inputId ? `${inputId}-state` : undefined;
  const describedBy = [helperText ? helperId : undefined, hasError || hasSuccess ? stateId : undefined]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`w-full ${containerClassName}`}>
      {label && (
        <label className="mb-2 block text-sm font-medium text-theme-text" htmlFor={inputId}>
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon ? <span className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-text-muted">{leftIcon}</span> : null}
        <input
          id={inputId}
          aria-invalid={hasError}
          aria-describedby={describedBy || undefined}
          className={`w-full rounded-xl border bg-theme-surface px-4 py-2.5 text-sm text-theme-text focus-visible:outline-none focus-visible:ring-2 ${stateClasses} ${
            leftIcon ? 'pl-10' : ''
          } ${rightIcon ? 'pr-10' : ''} ${className}`}
          {...props}
        />
        {rightIcon ? <span className="absolute right-3 top-1/2 -translate-y-1/2 text-theme-text-muted">{rightIcon}</span> : null}
      </div>
      {helperText ? (
        <p id={helperId} className="mt-1 text-xs text-theme-text-muted">
          {helperText}
        </p>
      ) : null}
      {hasError ? (
        <p id={stateId} className="mt-1 text-xs text-theme-danger">
          {error}
        </p>
      ) : null}
      {!hasError && hasSuccess ? (
        <p id={stateId} className="mt-1 text-xs text-theme-accent">
          {success}
        </p>
      ) : null}
    </div>
  );
};

export default Input;
